import React from "react";
import { Link } from "react-router-dom";
import { Button, Icon, PlainButton } from "@culturehq/components";

import { Trans } from "app/utils";

import QuickOptions from "components/display/QuickOptions";
import { TimeDiff } from "components/common/Time";

import ChqModalConfirm from "components/common/ChqModalConfirm";
import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import SurveyCompleted from "./SurveyCompleted";
import SurveyTableRow from "./SurveyTableRow";
import SurveyPreview from "./SurveyPreview";
import SurveyShare from "./SurveyShare";
import SurveyStatus from "./SurveyStatus";

const SurveyStatusDisplay = ({ survey: { activeModifiedAt }, status }) => {
  if (status.isDraft()) {
    return <i>{status.toString()}</i>;
  }
  return (
    <>
      {status.toString()}
      <br />
      <span className="SurveyTable__date">
        <TimeDiff time={activeModifiedAt} format="%b %e, %Y, %l:%M %p" />
      </span>
    </>
  );
};

const SurveyStatusDefaultAction = ({ survey, status, onSurveyEnded }) => {
  if (status.isActive()) {
    return (
      <ChqModalConfirm
        onAccept={() => onSurveyEnded(survey)}
        trigger={onTrigger => (
          <Button onClick={onTrigger} icon="close-circled" inverted>
            <Trans>End Survey</Trans>
          </Button>
        )}
      >
        <Trans>
          Are you sure you would like to end this survey? This survey will be
          removed from the home page and will not collect additional responses.
          You can always reactivate the survey from the Manage tab.
        </Trans>
      </ChqModalConfirm>
    );
  }
  if (status.isInactive()) {
    return (
      <SurveyCompleted survey={survey}>
        {onTrigger => (
          <Button icon="stats-bars" inverted onClick={onTrigger}>
            <Trans>Results</Trans>
          </Button>
        )}
      </SurveyCompleted>
    );
  }
  if (status.isDraft()) {
    return (
      <Link className="Button Button-inverted" to={`/surveys/${survey.id}/edit`}>
        <Icon icon="edit" /> <Trans>Edit</Trans>
      </Link>
    );
  }
  return null;
};

const SurveyManageableTableRow = ({
  survey,
  onSurveyActivated,
  onSurveyEnded,
  onSurveyDeleted
}) => {
  const status = SurveyStatus.from(survey);

  return (
    <SurveyTableRow key={survey.id} survey={survey} editable>
      <td>
        <SurveyStatusDisplay survey={survey} status={status} />
      </td>
      <td className="actions-cell">
        <QuickOptions>
          <ul className="dropdown-menu">
            {!status.isDraft() && (
              <li>
                <Link to={`/surveys/${survey.id}/edit`}>
                  <Icon icon="edit" />{" "}
                  <Trans>Edit Survey</Trans>
                </Link>
              </li>
            )}
            {status.isDraft() && (
              <li>
                <ChqModalConfirm
                  onAccept={() => onSurveyActivated(survey)}
                  trigger={onTrigger => (
                    <PlainButton onClick={onTrigger}>
                      <Icon icon="flash" />
                      <Trans>Activate</Trans>
                    </PlainButton>
                  )}
                >
                  <Trans>
                    Are you sure you would like to activate this survey?
                    Activating this survey will allow users to respond to it
                    from the survey page and the widget on the home page.
                  </Trans>
                </ChqModalConfirm>
              </li>
            )}
            {status.isInactive() && (
              <li>
                <ChqModalConfirm
                  onAccept={() => onSurveyActivated(survey)}
                  trigger={onTrigger => (
                    <PlainButton onClick={onTrigger}>
                      <Icon icon="flash" />
                      <Trans>Reactivate</Trans>
                    </PlainButton>
                  )}
                >
                  <Trans>
                    Are you sure you would like to reactivate this survey?
                    Reactivating this survey will allow users who have not
                    previously responded to it respond from the survey page and
                    the widget on the home page.
                  </Trans>
                </ChqModalConfirm>
              </li>
            )}
            <li>
              <ChqModalConfirmDelete
                onAccept={() => onSurveyDeleted(survey)}
                trigger={onTrigger => (
                  <PlainButton onClick={onTrigger}>
                    <Icon icon="ios-trash" />
                    <Trans>Delete Survey</Trans>
                  </PlainButton>
                )}
              >
                <Trans>
                  Are you sure you would like to delete this survey? This will
                  erase all data previously collected.
                </Trans>
              </ChqModalConfirmDelete>
            </li>
            <li>
              <SurveyPreview survey={survey}>
                {onTrigger => (
                  <PlainButton onClick={onTrigger}>
                    <Icon icon="clipboard" />
                    <Trans>View Preview</Trans>
                  </PlainButton>
                )}
              </SurveyPreview>
            </li>
            <li>
              <SurveyCompleted survey={survey}>
                {onTrigger => (
                  <PlainButton onClick={onTrigger}>
                    <Icon icon="stats-bars" />
                    <Trans>View Results</Trans>
                  </PlainButton>
                )}
              </SurveyCompleted>
            </li>
            <li>
              {status.isActive() && <SurveyShare survey={survey} />}
            </li>
          </ul>
        </QuickOptions>
        <SurveyStatusDefaultAction
          survey={survey}
          status={status}
          onSurveyEnded={onSurveyEnded}
        />
      </td>
    </SurveyTableRow>
  );
};

export default SurveyManageableTableRow;
